import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '../components/layout/AdminShell';
import { InviteUserForm } from '../components/users/InviteUserForm';
import { UserTable } from '../components/users/UserTable';
import { RoleManager } from '../components/roles/RoleManager';
import { PERMISSIONS } from '../lib/constants';
import { setAccountAlias } from '../lib/identityAuth';
import {
  fetchFacilities,
  fetchMe,
  fetchRoles,
  fetchUsers,
} from '../lib/identityRegistry';

const ALIAS_MAX_LENGTH = 64;

type AdminTab = 'overview' | 'users' | 'roles';

export function UserManagementPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<AdminTab>('overview');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [aliasOpen, setAliasOpen] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [pageMessage, setPageMessage] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    enabled: tab === 'users' || tab === 'overview',
  });
  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: fetchRoles,
    enabled: tab === 'users' || tab === 'roles',
  });
  const facilitiesQuery = useQuery({
    queryKey: ['facilities'],
    queryFn: fetchFacilities,
    enabled: tab === 'users',
  });

  const aliasMutation = useMutation({
    mutationFn: () => setAccountAlias(aliasDraft.trim()),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
      ]);
      setAliasError(null);
      setAliasOpen(false);
      setPageMessage('Display name updated.');
    },
    onError: (error) => {
      setAliasError(error instanceof Error ? error.message : 'Display name update failed');
    },
  });

  if (meQuery.isLoading) {
    return (
      <div className="route-skeleton route-skeleton--fullscreen">
        <div className="route-skeleton__pulse" />
        <p>Loading administration...</p>
      </div>
    );
  }

  if (meQuery.error || !meQuery.data) {
    return (
      <div className="center-screen">
        <div className="panel unauthorized">
          <h1>Unable to load profile</h1>
          <p className="panel__copy">
            {meQuery.error instanceof Error ? meQuery.error.message : 'Request failed'}
          </p>
        </div>
      </div>
    );
  }

  if (!meQuery.data.permissions.includes(PERMISSIONS.MANAGE_USERS)) {
    return (
      <div className="center-screen">
        <div className="panel unauthorized">
          <h1>Not authorized</h1>
          <p className="panel__copy">
            You need the <code>{PERMISSIONS.MANAGE_USERS}</code> permission to access user
            management.
          </p>
        </div>
      </div>
    );
  }

  const me = meQuery.data;

  const tabs: Array<{ id: AdminTab; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'roles', label: 'Roles' },
  ];

  function openAliasEditor() {
    setPageMessage(null);
    setAliasDraft(me.alias ?? '');
    setAliasError(null);
    setAliasOpen(true);
  }

  function closeAlias() {
    if (aliasMutation.isPending) return;
    setAliasOpen(false);
    setAliasError(null);
  }

  function handleAliasSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    aliasMutation.mutate();
  }

  return (
    <AdminShell subtitle={me.email}>
      <div className="admin-tabs">
        {tabs.map((entry) => (
          <button
            key={entry.id}
            className={`admin-tab${tab === entry.id ? ' admin-tab--active' : ''}`}
            type="button"
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <section className="panel admin-panel">
          <div className="panel-toolbar">
            <h2>Client overview</h2>
            <div className="panel-toolbar__actions">
              <button className="ghost-button" type="button" onClick={openAliasEditor}>
                Change name
              </button>
            </div>
          </div>
          <div className="admin-grid">
            <div className="stat-card">
              <p className="stat-card__label">Name</p>
              <p className="stat-card__value">{me.alias?.trim() ? me.alias : '—'}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Email</p>
              <p className="stat-card__value">{me.email}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Client type</p>
              <p className="stat-card__value">{me.client_type}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Your role</p>
              <p className="stat-card__value">{me.role_name}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Users</p>
              <p className="stat-card__value">{usersQuery.data?.length ?? '—'}</p>
            </div>
            <div className="stat-card stat-card--wide">
              <p className="stat-card__label">Permissions</p>
              <p className="stat-card__value">{me.permissions.join(', ') || '—'}</p>
            </div>
          </div>
          {pageMessage ? <p className="hint-text">{pageMessage}</p> : null}
        </section>
      ) : null}

      {tab === 'users' ? (
        <section className="panel admin-panel">
          <div className="panel-toolbar">
            <h2>Users</h2>
            <div className="panel-toolbar__actions">
              <button
                className="primary-button"
                type="button"
                onClick={() => setInviteOpen((open) => !open)}
              >
                {inviteOpen ? 'Close form' : 'Add user'}
              </button>
            </div>
          </div>

          {inviteOpen ? (
            rolesQuery.isLoading || facilitiesQuery.isLoading ? (
              <p className="hint-text">Loading invite form...</p>
            ) : (
              <InviteUserForm
                roles={rolesQuery.data ?? []}
                facilities={facilitiesQuery.data ?? []}
              />
            )
          ) : null}

          {usersQuery.isLoading || rolesQuery.isLoading || facilitiesQuery.isLoading ? (
            <p className="hint-text">Loading users...</p>
          ) : usersQuery.error ? (
            <div className="form-error">
              {usersQuery.error instanceof Error ? usersQuery.error.message : 'Failed to load users'}
            </div>
          ) : (
            <UserTable
              users={usersQuery.data ?? []}
              roles={rolesQuery.data ?? []}
              facilities={facilitiesQuery.data ?? []}
            />
          )}
        </section>
      ) : null}

      {tab === 'roles' ? (
        <section className="panel admin-panel">
          {rolesQuery.isLoading ? (
            <p className="hint-text">Loading roles...</p>
          ) : rolesQuery.error ? (
            <div className="form-error">
              {rolesQuery.error instanceof Error ? rolesQuery.error.message : 'Failed to load roles'}
            </div>
          ) : (
            <RoleManager roles={rolesQuery.data ?? []} />
          )}
        </section>
      ) : null}

      {aliasOpen ? (
        <div className="modal-backdrop" onClick={closeAlias}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h2>Change name</h2>
            <p className="modal-card__copy">
              This display name is shown instead of your email. Leave blank to clear.
            </p>
            <form className="invite-form" onSubmit={handleAliasSubmit}>
              <label className="form-field">
                <span>Display name</span>
                <input
                  type="text"
                  maxLength={ALIAS_MAX_LENGTH}
                  value={aliasDraft}
                  onChange={(event) => setAliasDraft(event.target.value)}
                  autoComplete="nickname"
                />
              </label>
              {aliasError ? <div className="form-error">{aliasError}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="ghost-button"
                  type="button"
                  disabled={aliasMutation.isPending}
                  onClick={closeAlias}
                >
                  Cancel
                </button>
                <button className="primary-button" disabled={aliasMutation.isPending} type="submit">
                  {aliasMutation.isPending ? 'Saving...' : 'Save name'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
